import {filter, forEach, find, includes, isEmpty, xorBy} from 'lodash';
import {useCallback, useContext, useEffect, useState} from 'react';

import {userApi} from 'api';
import {UserContext} from 'context';
import {usePrevious, useSessionReadyState} from 'hooks';
import {OnlineSession, ClientParticipant, OnlineSessionsStore} from 'interfaces';
import {ParentWindowNotifierService} from 'services';
import {onlineSessionsStore} from 'services/DataStore';
import {generateUtils, logger, sessionUtils} from 'utils';
import {getParticipantName} from 'utils/transform';

const VS_EVENT_TYPE = 'vs-online-sessions';
enum Events {
    JoinSession = 'vsJoinSession',
    SessionChanged = 'vsSessionChanged',
    SubscribeOnSessions = 'vsSubscribeOnSessions',
    SubscribeOnSessionsSuccess = 'vsSubscribeOnSessionsSuccess',
    SubscribeOnSessionsFailed = 'vsSubscribeOnSessionsFailed'
}

export const OnlineSessionsNotifier: React.FC = () => {
    const [sessions, setSessions] = useState<OnlineSession[]>([]);
    const [activeSessions, setActiveSessions] = useState<OnlineSession[]>([]);
    const prevActiveSessions = usePrevious(activeSessions);
    const [updatedSessions, setUpdatedSessions] = useState<OnlineSession[]>([]);
    const [subscriptionToken, setSubscriptionToken] = useState<string>(null);
    const {canJoin} = useSessionReadyState();
    const {settings} = useContext(UserContext);

    const formatPayload = useCallback((session: OnlineSession, pending: boolean) => {
        const client = find(session.participants, {role: 'client'}) as ClientParticipant;
        const fullName = getParticipantName(client?.data?.userInfo ?? {}, 'Unknown client');
        return {
            name: Events.SessionChanged,
            data: {
                pending,
                session: {
                    id: session.id
                },
                notification: {
                    label: fullName
                }
            }
        };
    }, []);

    const checkAuth = useCallback(async (payload: any): Promise<boolean> => {
        if (!payload) {
            return false;
        }

        try {
            const {valid} = await userApi.validateIntegrationCredentials(payload);
            return valid;
        } catch {
            return false;
        }
    }, []);

    const notify = useCallback((event: any) => {
        ParentWindowNotifierService.notify({
            type: VS_EVENT_TYPE,
            event
        });
    }, []);

    const handleJoinSession = useCallback(async (data: any) => {
        if (subscriptionToken) {
            const {session, token} = data || {};
            if (session?.id && token === subscriptionToken) {
                try {
                    const fullSession = find(sessions, (s) => s.id === session.id);
                    await sessionUtils.joinSession(fullSession, settings.featuresAvailability.assistantEnabled);
                } catch {
                    logger.warn(`Cannot join the session: ${session.id}`);
                }
            }
        }
    }, [subscriptionToken, sessions, settings.featuresAvailability.assistantEnabled]);

    const handleSubscribeOnSessions = useCallback(async (data) => {
        const auth = await checkAuth(data);
        if (auth) {
            const token = generateUtils.generateToken();
            setSubscriptionToken(token);
            notify({
                name: Events.SubscribeOnSessionsSuccess,
                data: {token}
            });
        } else {
            setSubscriptionToken(null);
            notify({
                name: Events.SubscribeOnSessionsFailed,
                data: {message: 'Authorization failed'}
            });
        }
    }, [checkAuth, setSubscriptionToken, notify]);

    const onMessageReceived = useCallback((e: MessageEvent) => {
        if (e.data.type !== VS_EVENT_TYPE) {
            return;
        }
        const {event: {name, data}} = e.data;

        if (name === Events.SubscribeOnSessions) {
            handleSubscribeOnSessions(data);
        } else if (name === Events.JoinSession) {
            handleJoinSession(data);
        }
    }, [handleSubscribeOnSessions, handleJoinSession]);

    useEffect(() => onlineSessionsStore.subscribe((payload: OnlineSessionsStore) => {
        const payloadSessions = payload.error ? [] : payload.sessions;
        setSessions(payloadSessions);
    }), [setSessions]);

    useEffect(() => {
        window.addEventListener('message', onMessageReceived);
        return () => {
            window.removeEventListener('message', onMessageReceived);
        };
    }, [onMessageReceived]);

    useEffect(() => {
        if (!subscriptionToken) {
            return;
        }
        const newAvailableForAnswerSessions = !canJoin ?
            [] :
            filter(sessions, (session) => sessionUtils.canJoinSession(session, settings.featuresAvailability.assistantEnabled));
        const newUpdatedSessions = xorBy<OnlineSession>(newAvailableForAnswerSessions, prevActiveSessions, 'id');
        if (newUpdatedSessions.length) {
            setActiveSessions(newAvailableForAnswerSessions);
            setUpdatedSessions(newUpdatedSessions);
        }
    }, [canJoin, sessions, subscriptionToken, prevActiveSessions, activeSessions, settings.featuresAvailability.assistantEnabled]);

    useEffect(() => {
        if (!subscriptionToken) {
            return;
        }
        if (!isEmpty(updatedSessions)) {
            forEach(updatedSessions, (session: OnlineSession) => {
                const isActive = includes(activeSessions, session);
                notify(formatPayload(session, isActive));
            });
        }

    }, [subscriptionToken, updatedSessions, activeSessions, notify, formatPayload]);

    return null;
};